/**
 * THE __gbzAuth SEAM — the bridge the auth pop-up (app.js openAuthModal) calls.
 *
 * This is the UI-side adapter Blake's pop-up restore ships (2026-08-31), and it
 * is deliberately a THIN one: every network call, payload rule, and ordering
 * decision below is Tyler's, copied from the surfaces he shipped and verified —
 * AuthContext.signIn / signUp are invoked UNCHANGED, and the two follow-up
 * sequences replicate register.tsx line for line, each annotated with its
 * source. If register.tsx's sequence changes, change this file to match; the
 * two must never drift apart on payload shape or ordering.
 *
 * WHY REPLICATION AND NOT A REFACTOR: Blake's ruling for this build is
 * "UI only — we must not break anything Tyler wired. This is absolutely
 * critical." Lifting the sequence out of register.tsx would have touched his
 * tested component; three small fetches copied verbatim with source
 * annotations keep his surface byte-identical while the modal gets the same
 * behavior.
 *
 * 🪤 BEARER, NOT THE COOKIE (register.tsx, the trap note above its PUT): the
 * server's requireAuth resolves the user from the Authorization header alone,
 * and the token must come off the register response, not from AuthContext
 * state — the React state is still null on the render that made this closure.
 *
 * 🪤 registeredToken RETRY-SAFETY (register.tsx:377): if signUp succeeded but
 * the shipping PUT failed, the visitor retries — and the retry must NOT
 * register twice. The module-level token mirrors his registeredToken state.
 */
import { useEffect } from "react";
import { useAuth, type BazaarUser } from "@/context/AuthContext";
import { appleSignIn, mountGoogleButton, preloadApple, type AppleCredential } from "./oauthWeb";

type SeamRegisterInput = {
  username: string;
  email: string;
  password: string;
  dateOfBirth: string;
  acceptedTerms: boolean;
  firstName: string;
  referralCode: string;
  categoryInterests: string[];
  acquisitionSource: string | null;
  address: { name: string; street1: string; city: string; state: string; zip: string };
};

/* register.tsx registeredToken semantics: survives a failed address PUT so the
   retry skips the register call. Module-level on purpose — the modal re-renders
   never remount this bridge.

   🔴 BOUND TO ONE EMAIL IN ONE SHEET (card 1219178426987205, a shared iPad on
   the show table). Kept for the life of the page and never compared to
   anything, it was handed to the NEXT person: person 1's address save failed,
   person 2 signed up in the same tab, and person 2's name and address were PUT
   onto person 1's account with person 1's token, then the page reloaded as
   person 1. So the token is reused only when the email is the one it was
   issued for (trimmed, lower-case, as the server normalizes it) AND the sheet
   it was made in is still the one on screen (`seamSheet`, app.js's own
   answer). Anything else forgets it and registers normally. */
let seamToken: string | null = null;
let seamEmail = "";
let seamSheet: () => boolean = () => false;
/* The token of a sign-up that stored this tab's session and whose ending (the
   reload, or the payout card) never ran: person 1's address save failed and
   person 1 walked away. Closing its sheet, the next sheet opened, or the next
   fresh register signs this tab out of it LOCALLY (clearLocalSession), so a
   refresh never boots the next person into person 1's account. A finished
   sign-up, or a normal sign-in, is never here and is never signed out. */
let unfinished: string | null = null;
const sameEmail = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/* What a sign-up that landed after its sheet was closed or reopened answers.
   app.js paints a register error only on the sheet that sent it, so this is
   never shown to the next person; it exists so the caller cannot read the
   stale result as a success and reload into that account. */
const STALE_SIGNUP =
  "Your account was created, but this sign-up was closed before it finished. Please sign in to finish setting it up.";

/* ── GOOGLE AND APPLE ON THE WEBSITE ──
   Nick's rule: Google or Apple replaces the email and password, NOTHING else.
   A new person still gives a date of birth (the server's age gate answers
   needsDateOfBirth first), then the sheet's steps 2-5: path, terms, interests
   and the shipping address, and a seller still gets the payout card. That is
   the mobile app's sequence (welcome.tsx DobCaptureModal, then register.tsx in
   OAuth mode: registration-details, then the blocking shipping PUT).

   The account exists from the date-of-birth call on, so "finished" is read
   from the account, not from this page: an account with no shipping address
   has not finished its sign-up (the signal mobile's registrationBackfillGate
   uses). Its token is held HERE and is not stored as this tab's session until
   oauthFinish has saved the rest of the sheet, so a sheet closed half way
   leaves nobody signed in, and the next Google or Apple sign-in to that
   account resumes at step 2. A finished account simply signs in. */
type OAuthConfig = { googleClientId: string | null; appleClientId: string | null };
type OAuthProvider = "google" | "apple";
type GoogleCredential = { idToken: string };

/* GET /api/auth/oauth/config, asked once per page (again only after a failure):
   null until it answers, and a null id keeps that button hidden. */
let oauthConfig: OAuthConfig | null = null;
let oauthAsking = false;
/* An unfinished account's session, held for oauthFinish (see above). */
let oauthToken: string | null = null;
let oauthUser: BazaarUser | null = null;
let oauthSheet: () => boolean = () => false;

const GOOGLE_FAILED = "Google sign-in failed. Please try again.";
const APPLE_FAILED = "Sign in with Apple failed. Please try again.";
const UNREACHABLE = "Could not reach the server. Please try again.";
/* the sentence a step-5 failure of register.tsx's OAuth mode says */
const DETAILS_FAILED = "Could not save your details. Please try again.";

/* A card that arrived before the config did gets its buttons now, unless the
   person is typing in it (a repaint would replace the field under the cursor). */
function repaintAuthCard(): void {
  const card = document.querySelector(".auth-card");
  if (!card) return;
  const a = document.activeElement;
  if (a && card.contains(a) && a.matches("input, select, textarea")) return;
  const w = window as Window & { rerenderKeepScroll?: () => void };
  if (typeof w.rerenderKeepScroll === "function") w.rerenderKeepScroll();
}

function askOauthConfig(): void {
  if (oauthConfig || oauthAsking) return;
  oauthAsking = true;
  const id = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
  fetch("/api/auth/oauth/config", { credentials: "include" })
    .then(async (res) => {
      if (!res.ok) return;
      const body = (await res.json()) as { googleClientId?: unknown; appleClientId?: unknown } | null;
      oauthConfig = { googleClientId: id(body?.googleClientId), appleClientId: id(body?.appleClientId) };
      if (oauthConfig.googleClientId || oauthConfig.appleClientId) repaintAuthCard();
    })
    .catch(() => {})
    .finally(() => {
      oauthAsking = false;
    });
}

/** Test seam only. */
export function __resetOauthSeamForTests(): void {
  oauthConfig = null;
  oauthAsking = false;
  oauthToken = null;
  oauthUser = null;
  oauthSheet = () => false;
}

/* register.tsx step 2, the BLOCKING shipping PUT, as both endings send it. */
async function putShippingAddress(
  token: string,
  a: { name: string; street1: string; city: string; state: string; zip: string },
): Promise<{ ok: true } | { error: string }> {
  let addressOk = false;
  /* A rate-limit refusal (429) carries the server's own sentence, which
     says to wait; every other refusal keeps register.tsx's sentence. */
  let addressError = "";
  try {
    const addrRes = await fetch("/api/users/me/shipping-address", {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        name: a.name,
        street1: a.street1,
        city: a.city,
        state: a.state,
        zip: a.zip,
      }),
    });
    addressOk = addrRes.ok;
    if (addrRes.status === 429) {
      const body = (await addrRes.json()) as { error?: unknown } | null;
      if (typeof body?.error === "string" && body.error.trim()) addressError = body.error;
    }
  } catch {
    addressOk = false;
  }
  if (!addressOk) {
    return { error: addressError || "Could not save your shipping address. Please check it and try again." };
  }
  return { ok: true };
}

export function AuthSeamBridge(): null {
  const { signIn, signUp, clearLocalSession, adoptSession, storedToken } = useAuth();

  useEffect(() => {
    const w = window as Window & { __gbzAuth?: unknown };

    /* asked while the page boots, so the buttons are known before anyone
       opens the sheet (a button appearing under a finger would move the one
       they meant to tap) */
    askOauthConfig();

    /* A new sheet (or a new person's register): forget the saved token, and
       sign this tab out of an unfinished sign-up's session. */
    const discardUnfinished = () => {
      if (unfinished) clearLocalSession(unfinished);
      unfinished = null;
      seamToken = null;
      seamEmail = "";
      seamSheet = () => false;
      oauthToken = null;
      oauthUser = null;
      oauthSheet = () => false;
    };

    w.__gbzAuth = {
      /** login.tsx:77 parity — identifier trimmed, password raw.
       *
       * 🔴 THE RELOAD IS OURS NOW, and the comment that used to sit here said the
       * opposite. app.js:6688 does `location.assign("/#/profile")` on success and
       * calls that a hard reload. It is not: the page is ALREADY on a hash route,
       * so changing only the hash is a same-document navigation and NOTHING
       * reloads. `authBusy` is never cleared, the button sits on "Signing in…"
       * forever, and the bundle never re-reads identity, which it only does at
       * boot. Found on staging 2026-09-01 by signing in: auth genuinely succeeded
       * and the UI hung until a manual refresh.
       *
       * We cannot fix it where it lives. app.js is Blake's file and is
       * byte-sacred; our layer works in src/web/* only. So the seam performs the
       * reload his handler intends.
       *
       * 🔑 setTimeout, NOT a microtask. His `.then` runs `location.assign` first
       * and we must not pre-empt it, or we reload without the hash and land on
       * the floor instead of the profile. A macrotask is queued behind his
       * synchronous continuation, so the order is: hash set, then reload.
       *
       * Only on SUCCESS. His handler already renders `res.error` in place and a
       * reload there would wipe the message the person needs to read.
       */
      signin: async (identifier: string, password: string) => {
        const res = await signIn(identifier.trim(), password);
        if (!res || !(res as { error?: unknown }).error) {
          window.setTimeout(() => window.location.reload(), 0);
        }
        return res;
      },

      /** register.tsx handleSubmit steps 1 + 2, verbatim order and payloads.
       *  Step 3 (Stripe) is split out below because Tyler's ruling puts a
       *  consent prompt between them, and the modal renders that prompt.
       *
       *  `isCurrent` is app.js's answer to "is the sheet that sent this still
       *  the one on screen?" (absent: always yes, the old single-sheet caller).
       *  🔴 A sign-up that lands after its sheet was closed or reopened belongs
       *  to nobody on screen: no session is stored for it (keepSession), no
       *  address is saved, its token is not kept for the payout card, and it
       *  answers STALE_SIGNUP so nothing reloads into it. The account exists on
       *  the server; that person can sign in later. */
      register: async (d: SeamRegisterInput, isCurrent: () => boolean = () => true) => {
        let token = seamToken && seamSheet() && sameEmail(seamEmail, d.email) ? seamToken : null;
        if (!token) {
          discardUnfinished();
          const result = await signUp({
            username: d.username,
            email: d.email,
            password: d.password,
            dateOfBirth: d.dateOfBirth,
            acceptedTerms: d.acceptedTerms,
            /* firstName/referralCode pass through RAW — AuthContext.signUp owns
               the omit-when-empty rule ("" would 400 on the server's min(1)). */
            firstName: d.firstName,
            referralCode: d.referralCode,
            categoryInterests: d.categoryInterests,
            ...(d.acquisitionSource ? { acquisitionSource: d.acquisitionSource } : {}),
          }, { keepSession: isCurrent });
          if (!isCurrent()) return { error: STALE_SIGNUP };
          if (result.error) return { error: result.error };
          token = result.token;
          seamToken = token;
          seamEmail = d.email;
          seamSheet = isCurrent;
          unfinished = token;
        }
        if (!token) {
          /* register.tsx's own sentence for the created-but-unverifiable case */
          return {
            error:
              "Your account was created but we could not verify the session. Please sign in and add your address from your profile.",
          };
        }

        /* ── the BLOCKING shipping PUT — register.tsx step 2, verbatim ── */
        return putShippingAddress(token, d.address);
      },

      /** Which of Google and Apple the website offers: GET /api/auth/oauth/config
       *  (false for both until it answers, so the code ships dark). */
      oauthProviders: () => {
        askOauthConfig();
        return { google: !!oauthConfig?.googleClientId, apple: !!oauthConfig?.appleClientId };
      },

      /** Google's own button into `el`; `onIdToken` gets the ID token. */
      oauthMountGoogle: (el: HTMLElement, onIdToken: (idToken: string) => void) => {
        if (oauthConfig?.googleClientId) mountGoogleButton(el, oauthConfig.googleClientId, onIdToken);
      },

      /** Apple's script, ahead of the tap that must open its pop-up. */
      oauthPreloadApple: () => {
        if (oauthConfig?.appleClientId) preloadApple();
      },

      /** Apple's pop-up: the credential, { cancelled: true }, or { error }. Call inside the tap. */
      oauthApple: async () => {
        if (!oauthConfig?.appleClientId) return { error: APPLE_FAILED };
        return appleSignIn(oauthConfig.appleClientId);
      },

      /** POST /api/auth/oauth/google|apple (mobile/lib/oauth.ts exchangeGoogle /
       *  exchangeApple's bodies), with the date of birth once the server has
       *  asked for it. Answers one of:
       *    { needsDateOfBirth: true }  a new person: ask for it and send again
       *    { finish: true }            the account exists, its sign-up is not
       *                                finished: the token is held for oauthFinish
       *    { signedIn: true }          a finished account: the session is stored
       *                                and the page reloads (the sign-in ending)
       *    { error }                   the server's own sentence (a closed,
       *                                suspended or beta-ended account, #1101)
       *  `isCurrent` is app.js's "is the sheet that sent this still on screen?":
       *  a sign-in that lands after it was closed or reopened keeps nothing. */
      oauthSignIn: async (
        provider: OAuthProvider,
        cred: GoogleCredential | AppleCredential,
        dateOfBirth: string | null,
        isCurrent: () => boolean = () => true,
      ) => {
        const failed = provider === "google" ? GOOGLE_FAILED : APPLE_FAILED;
        const dob = dateOfBirth ? { dateOfBirth } : {};
        const body =
          provider === "google"
            ? { idToken: (cred as GoogleCredential).idToken, clientType: "web", ...dob }
            : {
                identityToken: (cred as AppleCredential).identityToken,
                fullName: (cred as AppleCredential).fullName,
                email: (cred as AppleCredential).email,
                ...dob,
              };
        let data: {
          token?: string;
          user?: Record<string, unknown>;
          needsDateOfBirth?: boolean;
          error?: unknown;
        };
        try {
          const res = await fetch(`/api/auth/oauth/${provider}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(body),
          });
          data = (await res.json().catch(() => ({}))) as typeof data;
          if (!res.ok) {
            return { error: typeof data.error === "string" && data.error.trim() ? data.error : failed };
          }
        } catch {
          return { error: UNREACHABLE };
        }
        if (data.needsDateOfBirth) return { needsDateOfBirth: true };
        const token = typeof data.token === "string" ? data.token : "";
        const u = data.user;
        if (!token || !u) return { error: failed };
        if (!isCurrent()) return { error: STALE_SIGNUP };

        /* Finished? The shipping address is the sign-up's last required step.
           When the check cannot be made, a plain sign-in signs in (a failed
           check never keeps someone out), and an account made by this very
           call (it carried the date of birth) is treated as the new one it is. */
        let finished = !dateOfBirth;
        try {
          const me = await fetch("/api/users/me", {
            headers: { Authorization: `Bearer ${token}` },
            credentials: "include",
          });
          if (me.ok) {
            const row = (await me.json()) as { shippingAddressJson?: unknown } | null;
            finished = !!row?.shippingAddressJson;
          }
        } catch {
          /* keep the fallback above */
        }
        if (!isCurrent()) return { error: STALE_SIGNUP };

        const user: BazaarUser = {
          id: String(u.id),
          email: String(u.email),
          username: String(u.username),
          displayName: (u.displayName as string | null | undefined) ?? null,
          avatar: (u.avatar as string | null | undefined) ?? null,
          traderLevel: u.traderLevel as string | undefined,
          points: u.points as number | undefined,
          /* the OAuth answer carries no isAdmin; the reload reads the truth
             from /api/auth/me, as every web sign-in does */
          isAdmin: false,
        };
        if (finished) {
          discardUnfinished();
          adoptSession(token, user);
          /* the sign-in ending (signin above): app.js sets the hash, then this reloads onto it */
          window.setTimeout(() => window.location.reload(), 0);
          return { signedIn: true };
        }
        discardUnfinished();
        oauthToken = token;
        oauthUser = user;
        oauthSheet = isCurrent;
        return { finish: true };
      },

      /** The rest of the sheet for an account Google or Apple made: register.tsx's
       *  OAuth mode, verbatim order (registration-details, then the blocking
       *  shipping PUT). Only then is the session stored, and the payout card
       *  (stripeStart) works for it exactly as for an email sign-up. */
      oauthFinish: async (d: SeamRegisterInput, isCurrent: () => boolean = () => true) => {
        const token = oauthToken;
        const user = oauthUser;
        if (!token || !user || !oauthSheet()) return { error: STALE_SIGNUP };
        try {
          const det = await fetch("/api/users/me/registration-details", {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              categoryInterests: d.categoryInterests,
              ...(d.acquisitionSource ? { acquisitionSource: d.acquisitionSource } : {}),
              ...(d.referralCode.trim() ? { referralCode: d.referralCode.trim() } : {}),
            }),
          });
          if (!det.ok) return { error: DETAILS_FAILED };
        } catch {
          return { error: DETAILS_FAILED };
        }
        if (!isCurrent()) return { error: STALE_SIGNUP };
        const saved = await putShippingAddress(token, d.address);
        if ("error" in saved) return saved;
        if (!isCurrent() || oauthToken !== token) return { error: STALE_SIGNUP };
        adoptSession(token, user);
        seamToken = token;
        seamEmail = user.email;
        seamSheet = isCurrent;
        oauthToken = null;
        oauthUser = null;
        oauthSheet = () => false;
        return { ok: true };
      },

      /** app.js, whenever a sheet is opened or closed, and on entering the sign-up page. */
      discardUnfinished,

      /** app.js, as the sign-up's ending runs (the reload, or the payout card):
       *  the sign-up is finished, so the next sheet leaves its session alone.
       *  Answers whether this tab's stored session is still this sign-up's; a
       *  false is another tab having signed in since, and app.js must not
       *  reload into that account. */
      endSignUp: () => {
        unfinished = null;
        return !!seamToken && storedToken() === seamToken;
      },

      /** register.tsx launchStripeOnboarding, verbatim — including the
       *  ?ctx=web return/refresh params (load-bearing: those pages branch on
       *  it). On success the document navigates to stripe.com and no result
       *  ever reaches the caller; failure resolves {error} with both consent
       *  buttons left live (stripeLaunchFailed semantics). */
      stripeStart: async () => {
        const fail = {
          error:
            "Could not start Stripe setup. Your account is fine — try again, or skip and connect from your wallet.",
        };
        /* Only from the sheet the token was made in: a reopened sheet is the
           next person, and their payout setup must never open on this account. */
        if (!seamToken || !seamSheet()) return fail;
        /* ...and asked again after every wait, and just before leaving for
           Stripe: a sheet closed or reopened while a call was out is the next
           person, so this setup stops there and the tab stays put. */
        const sheet = seamSheet;
        const current = () => seamSheet === sheet && sheet();
        const auth = { Authorization: `Bearer ${seamToken}`, "Content-Type": "application/json" };
        try {
          const createRes = await fetch("/api/stripe/connect/accounts", {
            method: "POST",
            headers: auth,
            credentials: "include",
            body: JSON.stringify({
              email: seamEmail.trim().toLowerCase(),
              country: "US",
              businessType: "individual",
            }),
          });
          if (!current() || !createRes.ok) return fail;
          const { accountId } = (await createRes.json()) as { accountId?: string };
          if (!current() || !accountId) return fail;

          const origin = window.location.origin;
          const linkRes = await fetch(
            `/api/stripe/connect/accounts/${encodeURIComponent(accountId)}/onboarding-link`,
            {
              method: "POST",
              headers: auth,
              credentials: "include",
              body: JSON.stringify({
                returnUrl: `${origin}/stripe-connect-return?ctx=web`,
                refreshUrl: `${origin}/stripe-connect-refresh?ctx=web`,
              }),
            },
          );
          if (!current() || !linkRes.ok) return fail;
          const { url } = (await linkRes.json()) as { url?: string };
          if (!url) return fail;
          if (!current()) return fail;
          window.location.assign(url);
          /* navigating — leave the button disabled so a slow unload cannot
             double-create (register.tsx startStripeFromPrompt) */
          return {};
        } catch {
          return fail;
        }
      },
    };

    return () => {
      delete w.__gbzAuth;
    };
  }, [signIn, signUp, clearLocalSession, adoptSession, storedToken]);

  return null;
}
